import React, { useCallback, useEffect, useState, Component } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// Import slides
import { Slide1_Category } from '../components/slides/Slide1_Category';
import { Slide2_Problem } from '../components/slides/Slide2_Problem';
import { Slide3_System } from '../components/slides/Slide3_System';
import { SystemPage } from './SystemPage';
import { Slide_RecruitingDocs } from '../components/slides/Slide_RecruitingDocs';
import { Slide5_Forge } from '../components/slides/Slide5_Forge';
import { Slide5_Proof } from '../components/slides/Slide5_Proof';
import { Slide7_Tiers } from '../components/slides/Slide7_Tiers';
import { AdvantageProduct } from './AdvantageProduct';
import { AdvantageSummary } from './AdvantageSummary';
import { LeadershipPage } from './LeadershipPage';
import { ComparePage } from './ComparePage';
const SLIDES = [Slide1_Category, Slide2_Problem, Slide3_System, SystemPage, Slide_RecruitingDocs, Slide5_Forge, Slide5_Proof, Slide7_Tiers, AdvantageProduct, AdvantageSummary, LeadershipPage, ComparePage];
const SLIDE_LABELS = ['Capacity', 'Problem', 'Process', 'System', 'Frameworks', 'Sales School', 'Proof', 'Add-Ons', 'Partnership', 'Advantage', 'Leadership', 'Compare'];
export function PitchDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  // Warm the Google Drive connection up front so the Frameworks previews
  // aren't negotiating DNS and TLS at the moment the slide appears.
  useEffect(() => {
    const hosts = ['https://drive.google.com', 'https://lh3.googleusercontent.com'];
    const links = hosts.map(href => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      return link;
    });
    return () => links.forEach(link => link.remove());
  }, []);
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % SLIDES.length);
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + SLIDES.length) % SLIDES.length);
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const CurrentSlideComponent = SLIDES[currentSlide];
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 overflow-x-hidden relative selection:bg-[#7FE0C3]/30" data-id="element-1157" data-mp="25qc59dvvxfmw">
      {/* Main Content Area */}
      <div className="min-h-screen w-full relative flex flex-col" data-id="element-1158" data-mp="qcpthksrvwj8">
        <AnimatePresence mode="wait" data-id="element-1159" data-mp="udi3whdds7h3">
          <CurrentSlideComponent key={currentSlide} data-id="element-1160" data-mp="msflj26shcgv" />
        </AnimatePresence>

        {/* Spacer for bottom nav */}
        <div className="h-32 shrink-0" data-id="element-1161" data-mp="htl1jwx35hv6" />
      </div>

      {/* Navigation Controls (Bottom) - Mobile Optimized */}
      <div className="fixed bottom-0 left-0 w-full p-3 sm:p-6 flex flex-col space-y-3 z-50 pointer-events-none bg-gradient-to-t from-[#0A0F1C] via-[#0A0F1C]/90 to-transparent" data-id="element-1162" data-mp="wqvfre1451ul">
        {/* Slide Labels Navigation */}
        <div className="flex items-center justify-center pointer-events-auto" data-id="element-1163" data-mp="42ptunybp6t6">
          <div className="flex items-center space-x-1 sm:space-x-2 bg-slate-900/90 backdrop-blur-sm border border-slate-800 rounded-full px-3 sm:px-4 py-2 shadow-xl overflow-x-auto max-w-full scrollbar-hide" data-id="element-1164" data-mp="3v90nw04x17b0">
            {SLIDE_LABELS.map((label, idx) => <button key={idx} onClick={() => setCurrentSlide(idx)} className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap ${idx === currentSlide ? 'bg-[#7FE0C3] text-[#163A5F] shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`} aria-label={`Go to ${label}`} data-id="element-1165" data-mp="8z6ev9a87521" data-mpi={idx}>
                {label}
              </button>)}
          </div>
        </div>

        <p className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-slate-500" data-id="element-1166" data-mp="2thhml5e6tw5n">
          High Impact Recruiting Engine
        </p>

        {/* Logo and Arrow Controls */}
        <div className="flex items-center justify-between pointer-events-auto" data-id="element-1167" data-mp="33ffh2amn4p6p">
          <a href="https://thehireadvantage.co" target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity" data-id="element-1168" data-mp="37hitp2l1pztk">
            <img src="https://cdn.magicpatterns.com/uploads/f8eRPqMKBzam6b6SBPvfZ5/Full_white_logo.png" alt="HIRE" className="h-6 sm:h-8 hover:opacity-90 transition-opacity" data-id="element-1169" data-mp="1d3j2hxvnfmgl" />
          </a>

          <div className="flex items-center space-x-2" data-id="element-1170" data-mp="1ajv2l8zxveii">
            <button onClick={prevSlide} className="p-2 rounded-full hover:bg-slate-800 text-slate-500 hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Previous slide" data-id="element-1171" data-mp="15sms09mvhn3r">
              <ChevronLeft size={20} data-id="element-1172" data-mp="vscc2h9ge1kw" />
            </button>

            <button onClick={nextSlide} className="p-2 rounded-full hover:bg-slate-800 text-slate-500 hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Next slide" data-id="element-1173" data-mp="1tgmir9k6pdic">
              <ChevronRight size={20} data-id="element-1174" data-mp="1fixwbxagxyb6" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}